// Costs — quanto a frota custou. Dono do custo é o LiteLLM (docs/screens/12).
//
// Fonte única da tela: `litellm.spendByDay(janela)`, que é `GET /spend/logs`
// COM `start_date`/`end_date`. A janela muda a FORMA da resposta: vira agregado
// diário (3 linhas, 2 KB) em vez do log cru inteiro (14.452 linhas, 81 MB, 20-30 s,
// dos quais 61% é `metadata` e 32% são prompts e respostas). Total, gráfico
// diário, "By model" e "By agent" saem todos desse mesmo agregado.
//
// O único log cru que esta tela ainda toca é o de UMA chave
// (`litellm.spendForKey`, que exige `api_key`), na expansão de linha, e só
// depois do clique. Nada aqui chama /spend/logs sem janela ou sem chave.

import { repoAtual, gateway, litellm, SourceError, type Agent, type SpendDay, type SpendLog } from "../api";
import { empty, errorBox, esc, head, money, skeleton, table } from "../ui";

const CSS = `
.costs-sumario{display:flex;align-items:baseline;gap:16px;flex-wrap:wrap;margin:2px 0 18px}
.costs-grande{font-size:28px;font-weight:700}
.costs-muted{opacity:.6;font-size:12px}
.costs-grid{display:grid;grid-template-columns:65fr 35fr;gap:14px;align-items:start;margin-bottom:14px}
.costs-col{display:flex;flex-direction:column;gap:14px}
.costs-chart{display:flex;align-items:flex-end;gap:6px;height:230px}
.costs-day{flex:1 1 0;min-width:0;display:flex;flex-direction:column;justify-content:flex-end;height:100%;text-align:center}
.costs-day-total{font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.costs-bar{display:flex;flex-direction:column-reverse;width:100%}
.costs-seg{width:100%}
.costs-x{font-size:10px;opacity:.6;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.costs-dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:6px;vertical-align:baseline}
.costs-modelbar{display:flex;height:10px;border-radius:5px;overflow:hidden;margin-bottom:10px}
.costs-legenda{display:flex;flex-direction:column;gap:5px;font-size:12px}
.costs-legenda-linha{display:flex;justify-content:space-between;gap:10px}
.costs-run{cursor:pointer}
.costs-caret{display:inline-block;transition:transform .12s}
.costs-aberto .costs-caret{transform:rotate(90deg)}
.costs-det{font-size:12px;opacity:.75}
@media (max-width:900px){.costs-grid{grid-template-columns:1fr}}
`;
if (!document.getElementById("css-costs")) {
  const st = document.createElement("style");
  st.id = "css-costs";
  st.textContent = CSS;
  document.head.append(st);
}

// Cores client-side, chaveadas pela ordem de custo do agente — a mesma cor
// serve o dot de "By agent" e o segmento do gráfico. Paleta de modelo é outra,
// como no app de referência.
const CORES_AGENTE = ["#4ade80", "#facc15", "#2dd4bf", "#a3e635", "#fbbf24", "#34d399", "#eab308", "#5eead4", "#84cc16", "#14b8a6"];
const CORES_MODELO = ["#818cf8", "#f472b6", "#60a5fa", "#c084fc", "#f87171", "#38bdf8", "#e879f9", "#fb923c"];

/** Uma chave de `GET /key/list`. `token` é o hash — o mesmo identificador que
 *  aparece como campo extra em cada dia do agregado e como `api_key` no log. */
interface KeyInfo {
  token?: string;
  key_alias?: string | null;
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const usd = (v: number): string => money(Math.round(v * 100));
const iso = (d: Date): string => d.toISOString().slice(0, 10);
const rotuloDia = (d: string): string =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Soma um dos mapas do agregado (`users` ou `models`) em toda a janela. */
function somaDaJanela(dias: SpendDay[], campo: "users" | "models"): Map<string, number> {
  const out = new Map<string, number>();
  for (const d of dias) {
    for (const [k, v] of Object.entries(d[campo] ?? {})) {
      if (k) out.set(k, (out.get(k) ?? 0) + num(v));
    }
  }
  return out;
}

/** Nome de exibição: GET /v1/agents é o dono da frota; alias é o fallback. */
function nomeDoAgente(alias: string, agentes: Agent[]): string {
  const a = agentes.find((x) => x.agent_id === alias || x.agent_name === alias);
  return a?.agent_card_params?.name ?? a?.agent_name ?? alias;
}


/** Uma chamada no log cru de uma chave — só o que cabe numa linha de detalhe. */
const linhaDeLog = (l: SpendLog): string =>
  `<div>${esc(l.startTime?.slice(0, 19).replace("T", " ") ?? "")} · ${esc(l.model ?? "—")} · ${usd(num(l.spend))} · ${num(l.prompt_tokens)} tok</div>`;

// -------------------------------------------------------------------- página

/** Janela de tempo: estado local do select, não persiste em nada (contrato). */
let janelaDias: 7 | 30 = 7;

export async function costs(el: HTMLElement): Promise<void> {
  // Rota da área: #/custos, sem sub-rota — o hash só diz onde estamos.
  const header = head({
    title: "Costs",
    sub: "Total Anthropic spend from the cost ledger — task runs, dreams, goal routing and credential checks.",
    actions: `
      <select class="input" id="costs-window" style="width:auto">
        <option value="7"${janelaDias === 7 ? " selected" : ""}>Last 7 days</option>
        <option value="30"${janelaDias === 30 ? " selected" : ""}>Last 30 days</option>
      </select>`,
  });

  el.innerHTML = header + skeleton();

  try {
    el.innerHTML = header + (await corpo());
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "LiteLLM", err.message, err.next ?? "tente de novo");
  }

  el.querySelector<HTMLSelectElement>("#costs-window")?.addEventListener("change", (ev) => {
    janelaDias = (ev.target as HTMLSelectElement).value === "30" ? 30 : 7;
    costs(el);
  });

  // Expansão de linha: as chamadas daquela chave. É o único ponto da tela que
  // lê log cru, e ele vai filtrado por `api_key` — dezenas de linhas, não 81 MB.
  el.querySelectorAll<HTMLTableRowElement>(".costs-run").forEach((tr) =>
    tr.addEventListener("click", () => {
      const det = tr.nextElementSibling as HTMLElement | null;
      const chave = tr.dataset.key;
      if (!det || !chave) return;
      det.hidden = !det.hidden;
      tr.classList.toggle("costs-aberto");
      if (det.hidden || det.dataset.carregado) return;

      det.dataset.carregado = "1";
      const cel = det.firstElementChild as HTMLElement;
      cel.textContent = "…";
      litellm
        .spendForKey(chave)
        .then((logs) => {
          cel.innerHTML =
            logs.map(linhaDeLog).join("") || "Nenhuma chamada registrada nesta chave.";
        })
        .catch((e) => {
          cel.textContent = (e as SourceError).message;
        });
    }),
  );
}

async function corpo(): Promise<string> {
  const [dias, agentes, chaves] = await Promise.all([
    litellm.spendByDay(janelaDias),
    // Só nome de exibição: se a frota não responder, o alias cru é honesto.
    litellm.agents().catch(() => [] as Agent[]),
    // Custo por task = custo da chave etiquetada `task-{n}` (docs/screens/12).
    gateway<{ keys?: KeyInfo[] }>("litellm/key/list?return_full_object=true&size=100")
      .then((r) => r.keys ?? [])
      .catch(() => [] as KeyInfo[]),
  ]);

  // Total da janela = soma do `spend` de cada dia do agregado. Não somamos
  // linha de log: o agregado já vem somado pelo LiteLLM.
  const totalJanela = (dias ?? []).reduce((s, d) => s + num(d.spend), 0);

  const custoPorAgente = somaDaJanela(dias ?? [], "users");
  const custoPorModelo = somaDaJanela(dias ?? [], "models");
  const gastoDoDia = new Map<string, number>();
  const usersDoDia = new Map<string, Record<string, number>>();
  for (const d of dias ?? []) {
    const dia = (d.startTime ?? "").slice(0, 10);
    if (!dia) continue;
    gastoDoDia.set(dia, num(d.spend));
    usersDoDia.set(dia, d.users ?? {});
  }

  if (totalJanela === 0) {
    return empty(
      "Sem gasto na janela",
      "O ledger do LiteLLM não tem custo neste intervalo. Rode uma task para gerar gasto — ou alargue a janela.",
    );
  }

  // Ordem por custo desc define a cor de cada agente — dot e segmento iguais.
  const ordemAgentes = [...custoPorAgente.entries()].sort((a, b) => b[1] - a[1]);
  const corDoAgente = new Map(ordemAgentes.map(([alias], i) => [alias, CORES_AGENTE[i % CORES_AGENTE.length]]));

  // ---- sumário -------------------------------------------------------------
  // "N runs" e "avg $/run" saíram: run = sessão distinta, e sessão só existe no
  // log cru. Não há como contá-las sem baixar o histórico inteiro.
  const sumario = `
    <div class="costs-sumario">
      <span><span class="costs-grande">${usd(totalJanela)}</span> <span class="costs-muted">this window</span></span>
    </div>`;

  // ---- gráfico "Daily total" ----------------------------------------------
  // Altura do dia = `spend` do agregado; segmentos = `users` daquele dia.
  const fim = new Date();
  const inicio = new Date(fim.getTime() - (janelaDias - 1) * 86400000);
  const eixo: string[] = [];
  for (let t = inicio.getTime(); t <= fim.getTime(); t += 86400000) eixo.push(iso(new Date(t)));
  const maxDia = Math.max(0.000001, ...eixo.map((d) => gastoDoDia.get(d) ?? 0));

  const barras = eixo
    .map((d) => {
      const segs = ordemAgentes
        .map(([alias]) => {
          const v = num(usersDoDia.get(d)?.[alias]);
          return v
            ? `<div class="costs-seg" style="height:${(v / maxDia) * 185}px;background:${corDoAgente.get(alias)}" title="${esc(nomeDoAgente(alias, agentes))} ${usd(v)}"></div>`
            : "";
        })
        .join("");
      const t = gastoDoDia.get(d) ?? 0;
      return `
        <div class="costs-day">
          <div class="costs-day-total">${t ? usd(t) : ""}</div>
          <div class="costs-bar">${segs}</div>
          <div class="costs-x">${rotuloDia(d)}</div>
        </div>`;
    })
    .join("");

  const cardDiario = `
    <div class="card">
      <div class="card-head">
        <span class="card-title">Daily total</span>
        <span class="costs-muted">${usd(totalJanela)} this window</span>
      </div>
      <div class="card-body"><div class="costs-chart">${barras}</div></div>
    </div>`;

  // ---- "By agent" ----------------------------------------------------------
  // Cost = soma de `users` na janela. "Runs", "Avg/run" e "Cache hit" saíram:
  // sessão e token de cache só existem no log cru, que custa 81 MB.
  const linhasAgente = ordemAgentes.map(
    ([alias, custo]) => `<tr>
      <td><span class="costs-dot" style="background:${corDoAgente.get(alias)}"></span>${esc(nomeDoAgente(alias, agentes))}</td>
      <td><strong>${usd(custo)}</strong></td>
    </tr>`,
  );
  const cardAgente = `
    <div class="card">
      <div class="card-head"><span class="card-title">By agent</span></div>
      <div class="card-body">
        ${linhasAgente.length ? table(["Agent", "Cost"], linhasAgente) : '<p class="costs-muted">Nenhum gasto etiquetado por agente na janela.</p>'}
      </div>
    </div>`;

  // ---- "By model" ----------------------------------------------------------
  const ordemModelos = [...custoPorModelo.entries()].sort((a, b) => b[1] - a[1]);
  const totalModelos = ordemModelos.reduce((s, [, v]) => s + v, 0) || 1;
  const corDoModelo = new Map(ordemModelos.map(([m], i) => [m, CORES_MODELO[i % CORES_MODELO.length]]));
  const barraModelos = ordemModelos
    .map(([m, v]) => `<div style="width:${(v / totalModelos) * 100}%;background:${corDoModelo.get(m)}"></div>`)
    .join("");
  const legenda = ordemModelos
    .map(
      ([m, v]) => `
      <div class="costs-legenda-linha">
        <span><span class="costs-dot" style="background:${corDoModelo.get(m)}"></span>${esc(m)}</span>
        <span class="costs-muted">${usd(v)} · ${Math.round((v / totalModelos) * 100)}%</span>
      </div>`,
    )
    .join("");
  const cardModelo = `
    <div class="card">
      <div class="card-head"><span class="card-title">By model</span></div>
      <div class="card-body">
        ${ordemModelos.length ? `<div class="costs-modelbar">${barraModelos}</div><div class="costs-legenda">${legenda}</div>` : '<p class="costs-muted">Nenhum modelo no report da janela.</p>'}
      </div>
    </div>`;

  // ---- "Top tasks in window" ----------------------------------------------
  // Era "Top runs", que agrupava o log cru por sessão. A chave efêmera é por
  // task, e cada dia do agregado traz uma entrada por hash de chave — então o
  // gasto por task sai do mesmo agregado, cruzado com `key/list` pelo hash.
  const top = chaves
    .filter((k): k is KeyInfo & { token: string } => Boolean(k.token && k.key_alias))
    .map((k) => ({
      token: k.token,
      alias: String(k.key_alias),
      custo: (dias ?? []).reduce((s, d) => s + num(d[k.token]), 0),
    }))
    .filter((k) => k.custo > 0)
    .sort((a, b) => b.custo - a.custo)
    .slice(0, 10);

  // Task = issue: o número vem da etiqueta da chave efêmera, o título do GitHub.
  // Se o GitHub falhar, o alias cru ainda identifica a task — não derruba a página.
  const titulos = new Map<string, string>();
  try {
    const repo = await repoAtual();
    const numeros = [...new Set(top.map((r) => r.alias.match(/\d+/)?.[0]).filter((n): n is string => !!n))];
    await Promise.all(
      numeros.map(async (n) => {
        try {
          const i = await gateway<{ title: string }>(`github/repos/${repo}/issues/${n}`);
          titulos.set(n, i.title);
        } catch { /* issue apagada ou sem acesso: fica o alias */ }
      }),
    );
  } catch { /* sem repo resolvível: fica o alias */ }

  const linhasTask = top.flatMap((r) => {
    const issue = r.alias.match(/\d+/)?.[0];
    const task = (issue && titulos.get(issue)) ?? r.alias;
    return [
      `<tr class="costs-run" data-key="${esc(r.token)}">
        <td><span class="costs-caret">›</span> <strong>${usd(r.custo)}</strong></td>
        <td>${esc(task || "—")}</td>
      </tr>`,
      `<tr class="costs-det" hidden><td colspan="2"></td></tr>`,
    ];
  });
  const cardTasks = `
    <div class="card">
      <div class="card-head"><span class="card-title">Top tasks in window</span></div>
      <div class="card-body">
        ${linhasTask.length ? table(["Cost", "Task"], linhasTask) : '<p class="costs-muted">Nenhuma chave etiquetada com gasto na janela.</p>'}
      </div>
    </div>`;

  return (
    sumario +
    `<div class="costs-grid">${cardDiario}<div class="costs-col">${cardAgente}${cardModelo}</div></div>` +
    cardTasks
  );
}
